<template>
    <div>
        <DataTable :value="broodmares" lazy paginator dataKey="id"
                   v-model:rows="searchCriteria.page.limit" v-model:first="searchCriteria.page.offset"
                   removableSort :totalRecords="totalRecords" :loading="loading" @page="onPage"
                   @sort="onPage" :rowsPerPageOptions="[5,10,20,50]"
                   v-model:filters="searchCriteria"
                   filterDisplay="row" class="broodmare-table" scrollable
                   scrollHeight="calc(100vh - 300px)">
            <Column field="studbookRegistryId" :header="$t('message.broodmareList.table.studbookRegistryId')"
                    :sortable="true" filterField="studbookRegistryId"
                    :showFilterMenu="false" :showClearButton="false" class="w-1">
                <template #filter>
                    <CustomInputText name="studbookRegistryId" :maxLength="9" v-model="searchCriteria.studbookRegistryId.value" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="name" :header="$t('message.broodmareList.table.name')"
                    :sortable="true" filterField="name"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #filter>
                    <CustomInputText name="name" v-model="searchCriteria.name.value"
                                     class="mb-0" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="colorName" :header="$t('message.broodmareList.table.coatColor')"
                    :sortable="true" filterField="coatColorIds"
                    :showFilterMenu="false" :showClearButton="false" class="w-1">
                <template #filter>
                    <CustomMultiSelectHorseColor name="coatColorIds"
                                                 v-model="searchCriteria.coatColorIds.value"
                                                 item-label="name" @update:modelValue="search" />
                </template>
            </Column>
            <Column field="origin" :header="$t('message.broodmareList.table.origin')"
                    :sortable="true" filterField="origin"
                    :showFilterMenu="false" :showClearButton="false" class="w-1">
                <template #filter>
                    <CustomSelectOneHorseOrigin name="origin" v-model="searchCriteria.origin.value"
                                                @update:modelValue="search" />
                </template>
            </Column>
            <Column field="dateOfBirth" :header="$t('message.broodmareList.table.dateOfBirth')"
                    :sortable="true" filterField="name"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #filter>
                    <CustomCalendar name="dateOfBirth" v-model="searchCriteria.dateOfBirth.value" selectionMode="range"
                                    :showTime="false" :placeholder="$t('message.horseList.yearOrRange')" dateFormat="yy" view="year" showButtonBar
                                    @update:modelValue="search"/>
                </template>
            </Column>
            <Column field="previousStudbookRegistryId" :header="$t('message.broodmareList.table.originalStudbookRef')"
                    :sortable="true" filterField="previousStudbookRegistryId"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #filter>
                    <CustomInputText name="previousStudbookRegistryId" v-model="searchCriteria.previousStudbookRegistryId.value"
                                     @update:modelValue="search" />
                </template>
            </Column>
            <Column field="mareAgreements" :header="$t('message.broodmareList.table.coveringCertificate')"
                    :sortable="true" filterField="mareAgreements"
                    :showFilterMenu="false" :showClearButton="false" class="w-2">
                <template #filter>
                    <CustomInputText name="mareAgreements" v-model="searchCriteria.mareAgreements.value"
                                     @update:modelValue="search" />
                </template>
                <template #body="slotProps">
                    <div class="preserve-newlines">{{ slotProps.data.mareAgreements }}</div>
                </template>
            </Column>
            <Column field="offspringCount" :header="$t('message.broodmareList.table.offspringCount')"
                    :sortable="true" filterField="offspringCount"
                    :showFilterMenu="false" :showClearButton="false" class="w-1 text-center">
                <template #filter>
                    <div class="flex gap-1 align-items-center">
                        <CustomInputNumber name="offspringCountMin" v-model="searchCriteria.offspringCount.value.min"
                                           placeholder="Min..." @update:modelValue="search"/> -
                        <CustomInputNumber name="offspringCountMax" v-model="searchCriteria.offspringCount.value.max"
                                           placeholder="Max..." @update:modelValue="search"/>
                    </div>
                </template>
            </Column>
            <Column v-if="canGoToDetails" :header="$t('message.broodmareList.table.details')" class="w-1 text-center">
                <template #body="{ data }">
                    <router-link :to="`/horse/edit/${data.id}/covering`">
                        <i class="pi pi-arrow-right" />
                    </router-link>
                </template>
            </Column>
            <template #empty>
                {{ $t('message.other.emptyTable') }}
            </template>
        </DataTable>
    </div>
</template>

<script>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {FilterMatchMode} from "primevue/api";
import {
    searchBroodmares,
    searchBroodmaresCount,
} from "@/swagger/vue-api-client";
import CustomInputText from "@/components/form/CustomInputText.vue";
import CustomInputNumber from "@/components/form/CustomInputNumber.vue";
import {SearchCriteria} from "@/utils/SearchCriteria";
import CustomMultiSelectHorseColor from "@/components/form/CustomMultiSelectHorseColor.vue";
import CustomCalendar from "@/components/form/CustomCalendar.vue";
import CustomSelectOneHorseOrigin from "@/components/form/CustomSelectOneHorseOrigin.vue";
import {SystemRole} from "@/utils/SystemRole";

export default {
    name: "BroodmareTable",
    components: {
        CustomSelectOneHorseOrigin,
        CustomCalendar,
        Column,
        DataTable,
        CustomInputNumber,
        CustomInputText,
        CustomMultiSelectHorseColor,
    },

    props: {
        modelValue: null,
    },

    data() {
        return {
            loading: false,
            totalRecords: 0,
            broodmares: [],
            searchCriteria: this.getClearSearchCriteria(),
        };
    },

    mounted() {
        this.search();
    },

    computed: {
        canGoToDetails() {
            return ["ADMIN", "THIRD_LEVEL_EDITOR", "SECOND_LEVEL_EDITOR"].includes(SystemRole.getUserRole());
        },
    },

    methods: {
        search() {
            this.onPage(this.getFirstPage());
        },

        onPage(event) {
            this.loading = true;

            this.searchCriteria.page.offset = event.first;
            this.searchCriteria.page.limit = event.rows;
            this.searchCriteria.page.sortField = event.sortField;
            this.searchCriteria.page.sortOrder = event.sortOrder;

            const body = SearchCriteria.prepareCriteria(this.searchCriteria, true);

            searchBroodmares({body}).then((response) => {
                this.broodmares = response.data;
                return searchBroodmaresCount({body})
                    .then((response) => {
                        this.totalRecords = response.data;
                        this.loading = false;
                    });
            });
        },

        getFirstPage() {
            return {
                first: this.searchCriteria.page.offset,
                rows: this.searchCriteria.page.limit,
            };
        },

        getClearSearchCriteria() {
            return {
                studbookRegistryId: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: { value: null, matchMode: FilterMatchMode.CONTAINS },
                coatColorIds: { value: null, matchMode: FilterMatchMode.IN },
                dateOfBirth: { value: null, matchMode: FilterMatchMode.IN },
                previousStudbookRegistryId: { value: null, matchMode: FilterMatchMode.CONTAINS },
                mareAgreements: { value: null, matchMode: FilterMatchMode.IN },
                offspringCount: { value: {min: null, max: null}, matchMode: FilterMatchMode.IN },
                origin: { value: null, matchMode: FilterMatchMode.CONTAINS },
                page: {
                    limit: 10,
                    offset: 0,
                    sortField: null,
                    sortOrder: null,
                },
            };
        },
    },
};
</script>

<style lang="scss">
    .preserve-newlines {
        white-space: pre-line;
    }
</style>
